import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import {
    useGetAdsDataQuery,
    useGetSideQueriesQuery,
    useGetKeywordPresenceQuery,
} from 'src/context/api/visibility/api';
import { fDate } from 'src/utils/format-time';
import { AdItem, DomainItem } from '../types';
import { DEFAULT_API_PARAMS } from '../utils';

export const useAdsData = (selectedCountry: string | null, selectedDomain?: string, domainLimit = 40) => {
    const workspace: any = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);

    // Use dedicated PLA keywords for Ads page so that search term filters
    // applied in Ads do not impact other visibility pages using global keywords
    const keywordFilter: string[] = (filterStore?.keywordsPLA ?? []) as string[];

    const skipQuery = !filterStore.start || !filterStore.end || !workspace?.id;
    const skipAdsQuery = skipQuery || !selectedCountry;
    const currentCountryFilter = selectedCountry ? [selectedCountry] : [];

    // Use same date range as ads data so competitors query matches the selected period
    const sideStartISO: string | null = filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null;
    const sideEndISO: string | null = filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null;

    const { data: keywordPresenceData, isFetching: isFetchingKeywords } = useGetKeywordPresenceQuery({
        startDate: sideStartISO,
        endDate: sideEndISO,
        device: filterStore.devices?.map((device: string) => device.toLowerCase()) || [],
        countries: currentCountryFilter,
        domain: selectedDomain || '',
        workspaceTable: workspace?.bqTable,
        platform: filterStore?.platforms || [],
    }, { skip: skipAdsQuery || !selectedDomain || selectedDomain.trim() === '', refetchOnFocus: false, refetchOnReconnect: false });

    // Toutes les requêtes exécutées en parallèle pour optimiser les performances
    const [
        { data: sideQueriesData, isFetching: isFetchingSide },
        { data: AdsData, isFetching: isFetchingAdsData }
    ] = [
            useGetSideQueriesQuery({
                startDate: sideStartISO,
                endDate: sideEndISO,
                device: filterStore.devices?.map((device: string) => device.toLowerCase()) || [],
                countries: currentCountryFilter,
                keywords: keywordFilter,
                workspaceTable: workspace?.bqTable,
                workspaceId: workspace?.id,
                platform: filterStore?.platforms || [],
                // adType: [] // global view; if needed we can pass ['PLA'] or ['TEXT']
            }, { skip: skipAdsQuery, refetchOnFocus: false, refetchOnReconnect: false }),

            useGetAdsDataQuery({
                startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
                endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
                device: filterStore.devices?.map((device: string) => device.toLowerCase()) || [],
                countries: currentCountryFilter,
                keywords: keywordFilter,
                workspaceTable: workspace?.bqTable,
                workspaceId: workspace?.id,
                domain: "all",
                platform: filterStore?.platforms || []
            }, { skip: skipAdsQuery }),
        ];

    // Map unified ads data
    const AdItems: AdItem[] = useMemo(() => {
        if (!AdsData?.ads) return [];
        const hasKeywordFilter = (keywordFilter.length ?? 0) === 1;
        return AdsData.ads.map((ad: any) => {
            const avgPosition = typeof ad.avg_item_position === 'number' ? ad.avg_item_position : (typeof ad.position === 'number' ? ad.position : 0);
            const freqRaw = hasKeywordFilter
                ? (ad.frequency_non_weighted != null ? ad.frequency_non_weighted : 0)
                : (ad.frequency != null ? ad.frequency : (ad.impression_share != null ? ad.impression_share * 100 : 0));
            const adTypeRaw = String(ad.ad_type || '').toUpperCase();
            const positionTypeRaw = String(ad.position_type || '').toUpperCase();
            let adType: 'PLA' | 'TEXT' | 'RightPanel' = 'PLA';
            if (positionTypeRaw.includes('JACKPOT')) {
                adType = 'RightPanel';
            } else if (positionTypeRaw.includes('TXT') || adTypeRaw === 'TEXT' || adTypeRaw === 'TXT') {
                adType = 'TEXT';
            } else {
                adType = 'PLA';
            }
            return {
                title: ad.title || '',
                price: ad.price_label || ad.price || '',
                img: ad.image || undefined,
                searchTerm: ad.search_term || '',
                position: Number((avgPosition || 0).toFixed(0)),
                frequency: Math.round(freqRaw || 0),
                domain: ad.domain_name || ad.domain || '',
                _gcs_uri_desktop: ad._gcs_uri_desktop || null,
                _gcs_uri_mobile: ad._gcs_uri_mobile || null,
                device_type: ad.device_type || null,
                brandInfringementTitle: Boolean(ad.brand_infringement_title),
                brandInfringementDescription: Boolean(ad.brand_infringement_description),
                adType,
                description: ad.description || undefined,
                seller_name: ad.seller_name || undefined,
                platform: ad.platform || undefined,
                region_name: ad.region_name || undefined,
            };
        });
    }, [AdsData, keywordFilter]);

    // Map side queries top_competitors to DomainItem (value = paid_presence_rate)
    const domainItems: DomainItem[] = useMemo(() => {
        const rows = sideQueriesData?.top_competitors || [];
        if (!Array.isArray(rows) || rows.length === 0) return [];

        const aggregated = new Map<string, number>();
        rows.forEach((r: any) => {
            const domain = String(r?.domain_name || "").trim();
            if (!domain) return;
            const current = aggregated.get(domain) || 0;
            aggregated.set(domain, current + Number(r?.paid_presence_rate ?? 0));
        });

        return Array.from(aggregated.entries())
            .map(([domain, paidPresenceRate], index: number) => ({
                domain,
                paidPresenceRate,
                color: index === 0 ? '#4caf50' : undefined,
            }))
            .sort((a, b) => b.paidPresenceRate - a.paidPresenceRate)
            .slice(0, domainLimit);
    }, [sideQueriesData]);


    const keywordItems = useMemo(() => {
        const rows = keywordPresenceData?.keywords || [];
        if (!Array.isArray(rows) || rows.length === 0) return [];
        return rows
            .map((k: any) => ({
                label: k.search_term,
                score: Number(k.paid_presence_rate ?? 0),
            }))
            .sort((a, b) => b.score - a.score);
    }, [keywordPresenceData]);

    return {
        AdItems,
        domainItems,
        keywordItems,
        isFetchingKeywords,

        // Premier chargement
        isLoading: isFetchingSide || isFetchingAdsData,

        // Chargement lors d'un changement de filtre
        isFetching: isFetchingSide || isFetchingAdsData,
    };
};
